import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState('');
  const [activeTab, setActiveTab] = useState('specifications');

  useEffect(() => {
    fetchProduct();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchProduct = async () => {
    try {
      // Using the working API URL from your old code
      const response = await axios.get(`https://janvaahan.com/backend/public/api/products/${id}`);
      console.log('Product fetched:', response.data);
      setProduct(response.data);
      if (response.data.color) {
        setSelectedColor(response.data.color);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    if (!user) {
      navigate('/login', { state: { from: `/book/${id}` } });
    } else {
      navigate(`/book/${id}`);
    }
  };

  const handleTestRide = () => {
    if (!user) {
      navigate('/login', { state: { from: `/test-ride/${id}` } });
    } else {
      navigate(`/test-ride/${id}`);
    }
  };

  // Specifications data with fallbacks
  const specifications = {
    'Motor Power': product?.motor_power || '250W BLDC Hub Motor',
    'Battery Capacity': product?.battery_capacity ? `${product.battery_capacity} kWh` : '2.5 kWh Lithium-ion',
    'Range': `${product?.range || 80} km/charge`,
    'Top Speed': `${product?.top_speed || 75} km/h`,
    'Charging Time': product?.charging_time || '2-3 hours (0-80%)',
    'Battery Warranty': '5 years / 50,000 km',
    'Vehicle Warranty': '3 years / 50,000 km',
    'Brakes': 'Disc brakes (Front & Rear)',
    'Tyres': '90/90-17 Tubeless',
    'Weight': '108 kg',
    'Max Load': '150 kg'
  };

  const features = [
    {
      icon: '🔋',
      title: 'Advanced Battery Technology',
      description: 'High-density lithium-ion battery with intelligent BMS for optimal performance and longevity'
    },
    {
      icon: '⚡',
      title: 'Fast Charging',
      description: '80% charge in just 2 hours with our rapid charger technology'
    },
    {
      icon: '📱',
      title: 'Smart Connectivity',
      description: 'Bluetooth-enabled with mobile app for ride analytics and remote diagnostics'
    },
    {
      icon: '🛡️',
      title: 'Safety First',
      description: 'Advanced braking system with regenerative braking for maximum safety'
    },
    {
      icon: '🌱',
      title: 'Eco-Friendly',
      description: 'Zero emissions with regenerative braking that charges while you ride'
    },
    {
      icon: '🎨',
      title: 'Premium Design',
      description: 'Aerodynamic design with premium finish and LED lighting system'
    }
  ];

  const colors = ['Matte Black', 'Racing Red', 'Pearl White', 'Graphite Grey', 'Midnight Blue'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-6">🔍</div>
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Product Not Found</h2>
          <p className="text-gray-600 mb-8">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <div className="space-x-4">
            <Link 
              to="/products" 
              className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition"
            >
              Browse All Products
            </Link>
            <Link 
              to="/" 
              className="inline-block border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Product Image */}
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8">
                <img
                  src={product.image_url?.startsWith('http') ? product.image_url : `https://janvaahan.com/backend/public${product.image_url}`}
                  alt={product.name}
                  className="w-full h-auto object-contain transform hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/600x400?text=Janvahan+Bike';
                  }}
                />
              </div>
              
              {/* Color Options */}
              <div className="mt-6">
                <h3 className="text-sm font-medium mb-3 text-gray-700">Available Colors</h3>
                <div className="flex space-x-3">
                  {colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        selectedColor === color 
                          ? 'border-red-500 scale-110' 
                          : 'border-gray-300 hover:scale-105'
                      }`}
                      style={{
                        background: color === 'Matte Black' ? '#1a1a1a' :
                                   color === 'Racing Red' ? '#dc2626' :
                                   color === 'Pearl White' ? '#f5f5f5' :
                                   color === 'Graphite Grey' ? '#4b5563' :
                                   '#3b82f6'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div>
              <div className="mb-4">
                <span className="inline-block bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Electric Bike
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">{product.name}</h1>
              <p className="text-xl text-gray-400 mb-4">{product.model}</p>
              <div className="text-3xl font-bold text-red-500 mb-6">{product.price}</div>
              <p className="text-gray-300 mb-8 leading-relaxed">{product.description}</p>
              
              {/* Key Specs Quick View */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="border border-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl mb-1">⚡</div>
                  <div className="text-sm text-gray-400">Range</div>
                  <div className="font-bold text-white">{product.range || 80} km</div>
                </div>
                <div className="border border-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl mb-1">🚀</div>
                  <div className="text-sm text-gray-400">Top Speed</div>
                  <div className="font-bold text-white">{product.top_speed || 75} km/h</div>
                </div>
                <div className="border border-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl mb-1">🔋</div>
                  <div className="text-sm text-gray-400">Battery</div>
                  <div className="font-bold text-white">2.5 kWh</div>
                </div>
                <div className="border border-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl mb-1">⏱️</div>
                  <div className="text-sm text-gray-400">Charging</div>
                  <div className="font-bold text-white">2-3 hrs</div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex space-x-4">
                <button
                  onClick={handleBookNow}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition transform hover:scale-105"
                >
                  Book Now
                </button>
                <button
                  onClick={handleTestRide}
                  className="flex-1 border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-black transition transform hover:scale-105"
                >
                  Test Ride
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-2 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="border-b border-gray-200">
          <div className="flex space-x-8">
            {['specifications', 'features'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 text-lg font-semibold capitalize transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 border-red-600 text-red-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {/* Specifications Tab */}
          {activeTab === 'specifications' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-6">Technical Specifications</h3>
                <div className="space-y-4">
                  {Object.entries(specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600">{key}</span>
                      <span className="font-semibold text-gray-900">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-2xl font-bold mb-4">Performance Highlights</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-32 text-gray-600">Acceleration</div>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-red-600 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                      <span className="text-sm text-gray-500">0-40 km/h in 4.5 sec</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-32 text-gray-600">Efficiency</div>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-red-600 rounded-full" style={{ width: '95%' }}></div>
                      </div>
                      <span className="text-sm text-gray-500">95% motor efficiency</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-32 text-gray-600">Battery Life</div>
                    <div className="flex-1">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-red-600 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                      <span className="text-sm text-gray-500">1000+ charging cycles</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div>
              <h3 className="text-2xl font-bold mb-8">Key Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-lg transition">
                    <div className="text-4xl mb-4">{feature.icon}</div>
                    <h4 className="text-xl font-bold mb-2">{feature.title}</h4>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8">Compare Models</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition">
              <h3 className="text-xl font-bold mb-2">Janvahan JV1</h3>
              <p className="text-gray-600 mb-4">Perfect for city commuting</p>
              <p className="text-2xl font-bold text-red-600">₹99,999*</p>
              <Link to="/product/1" className="mt-4 inline-block text-red-600 hover:underline">View Details →</Link>
            </div>
            <div className="bg-white rounded-xl p-6 text-center border-2 border-red-600 shadow-lg">
              <h3 className="text-xl font-bold mb-2 text-red-600">{product.name}</h3>
              <p className="text-gray-600 mb-4">Current Model</p>
              <p className="text-2xl font-bold text-red-600">{product.price}</p>
              <button className="mt-4 text-red-600 hover:underline">View Details →</button>
            </div>
            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition">
              <h3 className="text-xl font-bold mb-2">Janvahan JV400</h3>
              <p className="text-gray-600 mb-4">Premium performance</p>
              <p className="text-2xl font-bold text-red-600">₹1,29,950*</p>
              <Link to="/product/4" className="mt-4 inline-block text-red-600 hover:underline">View Details →</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Financing Options */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl p-8 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">Easy Financing Options</h2>
              <p className="mb-6">Get your dream electric bike with flexible EMI options starting from ₹2,999/month</p>
              <button className="bg-white text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition">
                Calculate EMI
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-2xl font-bold">0%</div>
                <div>Interest EMI</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-2xl font-bold">24-60</div>
                <div>Months Tenure</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-2xl font-bold">₹2,999</div>
                <div>Starting EMI</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <div className="text-2xl font-bold">10+</div>
                <div>Bank Partners</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;